import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Minus, Plus, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      
      // Fetch related products from same category
      const relatedRes = await axios.get(`${API}/products?category=${response.data.category}&limit=4`);
      setRelatedProducts(relatedRes.data.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Ou dwe konekte pou ajoute nan panye');
      return;
    }

    try {
      await addToCart(product.id, quantity);
      toast.success(`${quantity}x ${product.name} ajoute nan panye!`);
    } catch (error) {
      toast.error('Erè pandan ajoute nan panye');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="animate-pulse bg-gray-200 aspect-square" />
            <div className="space-y-4">
              <div className="animate-pulse bg-gray-200 h-8 w-32" />
              <div className="animate-pulse bg-gray-200 h-12 w-64" />
              <div className="animate-pulse bg-gray-200 h-24 w-full" />
              <div className="animate-pulse bg-gray-200 h-10 w-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 text-center">
        <p className="text-xl text-muted-foreground">Pwodwi pa jwenn</p>
        <Link to="/products" className="text-mrc-navy hover:underline mt-4 inline-block">
          Retounen nan Pwodwi yo
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-mrc-navy mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Retounen nan Pwodwi yo
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Image */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white p-4"
          >
            <img 
              src={product.image_url} 
              alt={product.name}
              className="w-full aspect-square object-contain"
              data-testid="product-image"
            />
          </motion.div>

          {/* Details */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div>
              <Link 
                to={`/products?category=${product.category}`}
                className="text-sm text-mrc-orange uppercase tracking-wide hover:underline"
              >
                {product.category}
              </Link>
              <h1 className="font-heading text-4xl font-bold text-mrc-navy mt-2" data-testid="product-name">
                {product.name}
              </h1>
            </div>

            <p className="price text-3xl font-bold text-mrc-navy" data-testid="product-price">
              ${product.price.toFixed(2)}
            </p>

            <p className="text-muted-foreground leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              {product.stock > 0 ? (
                <>
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-green-600 font-medium">
                    {product.stock > 10 ? 'Disponib' : `Sèlman ${product.stock} rete`}
                  </span>
                </>
              ) : (
                <span className="text-red-600 font-medium">Pa disponib</span>
              )}
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Kantite</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    data-testid="decrease-qty"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="px-4 py-2 min-w-[60px] text-center font-mono" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    disabled={quantity >= product.stock}
                    data-testid="increase-qty"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className="w-full bg-mrc-navy hover:bg-mrc-navy/90 text-white py-6 text-lg font-semibold uppercase tracking-wider"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              Ajoute nan Panye
            </Button>

            {/* Features */}
            <div className="border-t pt-6 space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-mrc-orange" />
                <span>Livrezon disponib</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-mrc-orange" />
                <span>Garanti kalite</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-mrc-orange" />
                <span>Peman sekirize</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-16 pt-16 border-t">
            <h2 className="font-heading text-2xl font-bold text-mrc-navy mb-8">
              Pwodwi Menm Jan
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
